import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Beef, ClipboardList, ShoppingBasket, LogOut, ChefHat, Users, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, isPlatformOwner } from "@/lib/perms";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", perm: "dashboard:read" },
  { to: "/empresas", label: "Empresas", icon: Building2, testid: "nav-companies", perm: "companies:read" },
  { to: "/proteinas", label: "Proteínas & Preparações", icon: Beef, testid: "nav-proteins", perm: "proteins:read" },
  { to: "/cardapios", label: "Cardápios Semanais", icon: ClipboardList, testid: "nav-menus", perm: "menus:read" },
  { to: "/compras", label: "Lista de Compras", icon: ShoppingBasket, testid: "nav-purchases", perm: "purchase:read" },
  { to: "/usuarios", label: "Usuários", icon: Users, testid: "nav-users", perm: "users:read" },
];

const ROLE_BADGE_STYLES = {
  admin: "bg-emerald-100 text-emerald-800 border-emerald-200",
  gestor: "bg-sky-100 text-sky-800 border-sky-200",
  nutricionista: "bg-amber-100 text-amber-800 border-amber-200",
  visualizador: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Layout({ children }) {
  const { user, tenant, logout, can } = useAuth();
  const navigate = useNavigate();
  const visibleNav = NAV.filter((n) => can(n.perm));
  const platformOwner = isPlatformOwner(user);

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      <aside className="mp-sidebar hidden md:flex md:w-64 flex-col shrink-0" data-testid="sidebar">
        <div className="px-5 py-6 border-b border-slate-800/60">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-emerald-800 flex items-center justify-center">
              <ChefHat size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-semibold tracking-tight text-lg leading-tight">MenuPro</div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-400 truncate" data-testid="sidebar-tenant-name">{tenant?.name || "—"}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "active" : ""}`}
              >
                <Icon size={17} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
          {platformOwner && (
            <>
              <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Plataforma</div>
              <NavLink
                to="/plataforma"
                data-testid="nav-platform"
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "active" : ""}`}
              >
                <Shield size={17} />
                <span>Painel MenuPro</span>
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800/60">
          <div className="px-2 pb-2">
            <div className="text-xs text-slate-400 truncate">Logado como</div>
            <div className="text-sm text-white truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            {user?.role && (
              <span
                data-testid="sidebar-role-badge"
                className={`inline-block mt-2 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${ROLE_BADGE_STYLES[user.role] || ROLE_BADGE_STYLES.visualizador}`}
              >
                {ROLE_LABELS[user.role] || user.role}
              </span>
            )}
            {platformOwner && (
              <span
                data-testid="sidebar-platform-badge"
                className="inline-block mt-1 ml-1 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-200"
              >
                Dono MenuPro
              </span>
            )}
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut size={16} /> Sair
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-slate-900 text-white flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <ChefHat size={18} />
          <span className="font-semibold">MenuPro</span>
          <span className="text-xs text-slate-400 truncate max-w-[120px]">· {tenant?.name}</span>
        </div>
        <button data-testid="logout-btn-mobile" onClick={() => { logout(); navigate("/login"); }} className="text-slate-300">
          <LogOut size={18} />
        </button>
      </div>

      <main className="flex-1 min-w-0">
        <div className="md:hidden mt-14 px-3 py-2 bg-white border-b border-slate-200 overflow-x-auto whitespace-nowrap">
          <div className="inline-flex gap-1">
            {visibleNav.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"} data-testid={`${n.testid}-mobile`}
                className={({ isActive }) => `text-xs px-3 py-1.5 rounded-full border ${isActive ? "bg-emerald-900 text-white border-emerald-900" : "border-slate-200 text-slate-700"}`}
              >
                {n.label}
              </NavLink>
            ))}
            {platformOwner && (
              <NavLink
                to="/plataforma" data-testid="nav-platform-mobile"
                className={({ isActive }) => `text-xs px-3 py-1.5 rounded-full border ${isActive ? "bg-amber-600 text-white border-amber-600" : "border-amber-300 text-amber-800 bg-amber-50"}`}
              >
                Painel MenuPro
              </NavLink>
            )}
          </div>
        </div>
        <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1400px]">{children}</div>
      </main>
    </div>
  );
}
